import React, {useState, useRef, useEffect, useCallback} from 'react'
import _ from 'lodash'
import {Spinner} from 'reactstrap'
import { Stage, Layer, Image, Transformer, Rect, Group} from "react-konva";
import {
    actionUpdateBgImage,
    actionUpdateBgImageRef, actionUpdateLoadingBackground
} from '../../context/SelectedVariantContext/action'
import {useSelectedVariantContext} from '../../context/SelectedVariantContext'
import { measure } from '../../../../utils/perf'

// here is another way to update the image
class LayerBgImageClass extends React.Component {
    state = {
        image: null,
        isLoading: false,
        error: null
    }

    componentDidMount() {
        this.loadImage()
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.background !== this.props.background) {
            this.loadImage()
        }
    }

    componentWillUnmount() {
        // Clean up event listeners
        this.cleanup()
    }

    cleanup() {
        if (this.image) {
            this.image.removeEventListener('load', this.handleLoad)
            this.image.removeEventListener('error', this.handleError)
            // Cancel image loading if in progress
            if (this.state.isLoading) {
                this.image.src = ''
            }
            this.image = null
        }
    }

    loadImage() {
        // Clean up previous image before loading new one
        this.cleanup()
        
        const { background } = this.props
        
        if (!background) {
            this.setState({
                image: null,
                isLoading: false,
                error: 'No background provided'
            })
            return
        }
        
        this.setState({ isLoading: true, error: null })
        
        // Create a promise to handle image loading
        const loadPromise = new Promise((resolve, reject) => {
            this.image = new window.Image()
            this.image.crossOrigin = 'Anonymous'
            
            const handleLoadSuccess = () => {
                this.image.removeEventListener('load', handleLoadSuccess)
                this.image.removeEventListener('error', handleLoadError)
                resolve()
            }
            
            const handleLoadError = (error) => {
                this.image.removeEventListener('load', handleLoadSuccess)
                this.image.removeEventListener('error', handleLoadError)
                reject(error)
            }
            
            // Add event listeners BEFORE setting src
            this.image.addEventListener('load', handleLoadSuccess)
            this.image.addEventListener('error', handleLoadError)
            
            // Set src AFTER adding listeners
            this.image.src = background
            
            // Check if image is already loaded (from cache)
            // This must be done AFTER setting src
            if (this.image.complete) {
                if (this.image.naturalHeight !== 0) {
                    // Image loaded successfully from cache
                    handleLoadSuccess()
                } else {
                    // Image failed to load from cache
                    handleLoadError(new Error('Image failed to load from cache'))
                }
            }
        })
        
        // Handle the promise
        loadPromise
            .then(() => {
                this.handleLoad()
            })
            .catch((error) => {
                this.handleError(error)
            })
    }

    handleLoad = () => {
        // Double-check to prevent duplicate calls
        if (!this.state.isLoading) return
        
        this.setState({
            image: this.image,
            isLoading: false,
            error: null
        }, () => {
            if (this.props.onLoaded && this.image) {
                this.props.onLoaded(this.image);
            }
        })
    }

    handleError = (error) => {
        this.setState({
            image: null,
            isLoading: false,
            error: 'Failed to load background image'
        })
    }

    render() {
        const {wRect, hRect, xPosition, yPosition, innerRef} = this.props
        const {image, error} = this.state
        
        // Show fallback if image failed to load or is still loading initially
        if (error || (!image && !this.state.isLoading)) {
            return (
                <Rect
                    width={200}
                    height={200}
                    x={0}
                    y={0}
                    fill="#f0f0f0"
                    opacity={0.3}
                    ref={innerRef}
                />
            );
        }
        
        // Show loading state while image is loading
        if (!image && this.state.isLoading) {
            return (
                <Rect
                    width={200}
                    height={200}
                    x={0}
                    y={0}
                    fill="#e8e8e8"
                    opacity={0.5}
                    ref={innerRef}
                />
            );
        }
        
        return (
            <Image
                image={image}
                width={200}
                height={200}
                x={0}
                y={0}
                ref={innerRef}
            />
        );
    }
}

const LayerBgImage = React.forwardRef((props, ref) => {
   return <LayerBgImageClass innerRef={ref} {...props} />
})




// LayerImage component without caching
class LayerImage extends React.Component {
    state = {
        image: null,
        isLoading: false,
        error: null
    }

    componentDidMount() {
        this.loadImage()
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.background !== this.props.background) {
            this.loadImage()
        }
    }

    componentWillUnmount() {
        // Clean up event listeners
        if (this.image) {
            this.image.removeEventListener('load', this.handleLoad)
            this.image.removeEventListener('error', this.handleError)
        }
    }

    loadImage() {
        const { background } = this.props;

        // Set loading state
        this.setState({ isLoading: true, error: null }, () => {
            this.props.setLoading(true)
        });

        // Clean up previous image listeners if exists
        if (this.image) {
            this.image.removeEventListener('load', this.handleLoad)
            this.image.removeEventListener('error', this.handleError)
        }

        // Create new image
        this.image = new window.Image()
        this.image.src = background
        this.image.crossOrigin = 'Anonymous'

        // Add event listeners
        this.image.addEventListener('load', this.handleLoad)
        this.image.addEventListener('error', this.handleError)
    }

    handleLoad = () => {
        this.setState({
            image: this.image,
            isLoading: false,
            error: null
        }, () => {
            this.props.setLoading(false)
        });
    }

    handleError = (error) => {
        this.setState({
            image: null,
            isLoading: false,
            error: 'Failed to load image'
        }, () => {
            this.props.setLoading(false)
        });
    }

    render() {
        const {width, height, wPreview, hPreview} = this.props
        const {image, isLoading, error} = this.state

        // Show loading state with a better visual indicator
        if (isLoading) {
            return (
                <React.Fragment>
                    <Rect
                        width={width}
                        height={height}
                        x={(wPreview-200)/2}
                        y={(hPreview-200)/2}
                        fill="#e0e0e0"
                        opacity={0.5}
                    />
                    {/* Add a pulsing effect for loading */}
                    <Rect
                        width={width}
                        height={height}
                        x={(wPreview-200)/2}
                        y={(hPreview-200)/2}
                        fill="#007bff"
                        opacity={0.1}
                    />
                </React.Fragment>
            );
        }

        // Show error state
        if (error || !image) {
            return (
                <Rect
                    width={width}
                    height={height}
                    x={(wPreview-200)/2}
                    y={(hPreview-200)/2}
                    fill="#f5f5f5"
                    opacity={0.8}
                    stroke="#ddd"
                    strokeWidth={1}
                />
            );
        }

        return (
            <Image
                image={image}
                width={width}
                height={height}
                x={(wPreview-200)/2}
                y={(hPreview-200)/2}
            />
        );
    }
}

// Memoized version to prevent unnecessary re-renders
const MemoizedLayerImage = React.memo(LayerImage, (prevProps, nextProps) => {
    return prevProps.background === nextProps.background &&
           prevProps.width === nextProps.width &&
           prevProps.height === nextProps.height &&
           prevProps.wPreview === nextProps.wPreview &&
           prevProps.hPreview === nextProps.hPreview;
});

const Rectangle = ({ shapeProps, isSelected, onSelect, onChange, onChangeState }) => {
    const shapeRef = React.useRef();
    const trRef = React.useRef();

    React.useEffect(() => {
        if (isSelected) {
            // we need to attach transformer manually
            trRef.current.nodes([shapeRef.current]);
            trRef.current.getLayer().batchDraw();
        }
    }, [isSelected]);

    return (
        <React.Fragment>
            <Rect
                onClick={onSelect}
                onTap={onSelect}
                ref={shapeRef}
                {...shapeProps}
                draggable
                onDragMove={(e) => {
                    onChange({
                        ...shapeProps,
                        x: e.target.x(),
                        y: e.target.y(),
                    });
                }}
                onDragEnd={(e) => {
                    onChangeState({
                        ...shapeProps,
                        x: e.target.x(),
                        y: e.target.y(),
                    });
                }}
                onTransformEnd={() => {
                    const node = shapeRef.current;
                    const scaleX = node.scaleX();
                    const scaleY = node.scaleY();

                    // we will reset it back
                    node.scaleX(1);
                    node.scaleY(1);
                    onChangeState({
                        ...shapeProps,
                        x: node.x(),
                        y: node.y(),
                        // set minimal value
                        width: Math.max(5, node.width() * scaleX),
                        height: Math.max(node.height() * scaleY),
                    });
                }}
            />
            {isSelected && (
                <Transformer
                    ref={trRef}
                    name="transformer"
                    rotateEnabled={false}
                    flipEnabled={false}
                    boundBoxFunc={(oldBox, newBox) => {
                        // limit resize
                        if (Math.abs(newBox.width) < 5 || Math.abs(newBox.height) < 5) {
                            return oldBox;
                        }
                        return newBox;
                    }}
                />
            )}
        </React.Fragment>
    );
};

const PreviewBackgroundImage = (props) => {
    const {
        state: {currentBgImage, currentBgImageType, product, selectedAttribute},
        dispatch
    } = useSelectedVariantContext()
    const {mockups} = product
    let mockupInfos = _.get(selectedAttribute?.mockup, 'meta.mockup_infos', [])
    const stageRef = useRef();
    const bgImageRef = useRef(null);
    const groupRef = useRef();
    const [loadingMockup, setLoadingMockup] = useState(true)
    const [bgImageLoaded, setBgImageLoaded] = useState(false)
    const {imageProp, reloadBgPreview, index, wPreview = 200, hPreview = 200} = props
    const _handleSetLoading = (flag) => {
        setLoadingMockup(flag)
    }

    const [xPosition, setXPosition] = useState(currentBgImage?.x || (wPreview - 200) / 2)
    const [yPosition, setYPosition] = useState(currentBgImage?.y || (hPreview - 200) / 2)
    const [wRect, setWRect] = useState(wPreview)
    const [hRect, setHRect] = useState(hPreview)
    const [selectedId, selectShape] = React.useState(`rect${index}`);

    const onChangeBackground = useCallback(async () => {
        if (!bgImageRef?.current) return

        try {
            const base64 = await measure('canvas.preview_bg', 'canvas.capture', () => bgImageRef.current.toDataURL({
                x: (wPreview - 200) / 2,
                y: (hPreview - 200) / 2,
                pixelRatio: 5,
                width: 200,
                height: 200
            }), { tags: { purpose: 'preview', pixel_ratio: 5 } })
            dispatch(actionUpdateBgImageRef(base64))
        } catch (error) {

        }
    }, [wPreview, hPreview, dispatch])

    const onLoadedBackgroundImage = useCallback((image) => {
        if (bgImageRef.current && image) {
            try {
                // Set the image to the Konva Image node
                bgImageRef.current.image(image)
                // Mark as loaded
                setBgImageLoaded(true)
                // Delay to ensure image is rendered before generating dataURL
                setTimeout(() => {
                    onChangeBackground()
                    dispatch(actionUpdateLoadingBackground(false))
                }, 100)
            } catch (error) {
                dispatch(actionUpdateLoadingBackground(false))
            }
        } else {

        }
    }, [dispatch, onChangeBackground, index])

    useEffect(()=> {
        if (bgImageRef?.current && Object.keys(currentBgImage).length > 0) {
            bgImageRef.current.width(currentBgImage?.width)
            bgImageRef.current.height(currentBgImage?.height)
            bgImageRef.current.x(currentBgImage?.x)
            bgImageRef.current.y(currentBgImage?.y)
        }
    }, [currentBgImage])

    const onChangeBgState = (newAttrs) => {
        const {id, ...restAttrs} = newAttrs;
        setXPosition(restAttrs.x)
        setYPosition(restAttrs.y)
        setWRect(restAttrs.width)
        setHRect(restAttrs.height)
       dispatch(actionUpdateBgImage({
            ...currentBgImage,
            ...restAttrs
        }))
    }

    const onChangeBg = (newAttrs) => {
        setXPosition(newAttrs.x)
        setYPosition(newAttrs.y)
        setWRect(newAttrs.width)
        setHRect(newAttrs.height)
    }

    useEffect(() => {
        setXPosition(currentBgImage.x || (wPreview - 200) / 2)
        setYPosition(currentBgImage.y || (hPreview - 200) / 2)
        setWRect(currentBgImage.width || 200)
        setHRect(currentBgImage.height || 200)
        // Only call onChangeBackground if image has been loaded
        if (bgImageLoaded && bgImageRef?.current) {
            onChangeBackground()
        }
    }, [currentBgImage, wPreview, hPreview, bgImageLoaded, onChangeBackground])

    return (
        <>
            <Stage
                className="bgImage"
                ref={stageRef}
                width={200}
                height={200}
                onMouseDown={e => {
                    // deselect when clicked on empty area
                    const clickedOnEmpty = e.target === e.target.getStage();
                    if (clickedOnEmpty) {
                        selectShape(null);
                    }
                }}
            >
                <Layer>
                    <Group
                        ref={groupRef}
                        clipFunc={(ctx) => {
                            ctx.save();
                            ctx.rect((wPreview - 200) / 2, (hPreview - 200) / 2, 200, 200);
                            ctx.restore();
                        }}
                        x={0}
                        y={0}
                        height={200}
                        width={200}
                    >
                        <LayerBgImage
                            ref={bgImageRef}
                            background={currentBgImage?.file || mockupInfos[index]?.parts?.[0]?.image_path}
                            // currentBgImageType={currentBgImageType}
                            xPosition={xPosition}
                            yPosition={yPosition}
                            hRect={hRect}
                            wRect={wRect}
                            // currentBgImage={currentBgImage}
                            onLoaded={onLoadedBackgroundImage}
                        />
                        <MemoizedLayerImage
                            background={imageProp}
                            width={200}
                            height={200}
                            hPreview={200}
                            wPreview={200}
                            setLoading={_handleSetLoading}
                        />
                    </Group>
                    {(currentBgImageType === 'original' && currentBgImage.file) && (
                        <React.Fragment>
                            <Rectangle
                                shapeProps={{
                                    x: xPosition,
                                    y: yPosition,
                                    width: wRect,
                                    height: hRect,
                                    // fill: 'green',
                                    id: `rect${index}`,
                                }}
                                isSelected={selectedId === `rect${index}`}
                                onSelect={() => {
                                    selectShape(`rect${index}`);
                                }}
                                onChangeState={onChangeBgState}
                                onChange={onChangeBg}
                            />
                        </React.Fragment>
                    )}
                </Layer>
            </Stage>
            {(reloadBgPreview || loadingMockup) && (
                <div className='ImageFirstLoading loadingBackground text-center'>
                    <Spinner style={{color: '#007bff'}} />
                </div>
            )}
        </>
    );
};

export default PreviewBackgroundImage
